import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, IdCard, Loader2, Lock, Mail, ShieldCheck, Ticket, User } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePartnerAuth, formatApiError } from "@/context/PartnerAuthContext";

export default function PartnerAuth() {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const inviteToken = params.get("invite") || "";
    const [mode, setMode] = useState(inviteToken ? "signup" : "signin"); // signin | signup
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [legalName, setLegalName] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [loading, setLoading] = useState(false);
    const [invite, setInvite] = useState(null); // { valid, email, expires_at, reason }
    const [inviteChecking, setInviteChecking] = useState(!!inviteToken);
    const { signin, signup } = usePartnerAuth();
    const navigate = useNavigate();
    const next = params.get("next") || "/partner/dashboard";

    // Resolve the invite token (if any) → prefill + lock email, surface a banner.
    useEffect(() => {
        if (!inviteToken) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/partner/invites/${inviteToken}`);
                if (cancelled) return;
                setInvite(data);
                if (data.valid && data.email) {
                    setEmail(data.email);
                    setMode("signup");
                }
            } catch (err) {
                if (!cancelled) {
                    setInvite({ valid: false, reason: "not_found", email: null });
                }
            } finally {
                if (!cancelled) setInviteChecking(false);
            }
        })();
        return () => { cancelled = true; };
    }, [inviteToken]);

    const submit = async (e) => {
        e.preventDefault();
        if (mode === "signup" && password !== confirm) {
            toast.error("Passwords do not match");
            return;
        }
        if (mode === "signup" && legalName.trim().length < 2) {
            toast.error("Legal Name is required (name on your government-issued ID).");
            return;
        }
        setLoading(true);
        try {
            const data =
                mode === "signin"
                    ? await signin(email, password)
                    : await signup(email, password, {
                          legal_name: legalName.trim(),
                          display_name: displayName.trim() || undefined,
                          invite_token: inviteToken || undefined,
                      });
            toast.success(
                mode === "signin" ? "Welcome back" : "Account created",
            );
            if (data.agreement_accepted) navigate(next);
            else navigate(`/partner/agreement?next=${encodeURIComponent(next)}`);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    const inviteBanner = () => {
        if (!inviteToken) return null;
        if (inviteChecking) {
            return (
                <div
                    className="mb-6 rounded-sm border border-gold/40 bg-gold/10 px-4 py-3 text-forest text-xs flex items-center gap-2"
                    data-testid="partner-invite-checking"
                >
                    <Loader2 size={14} className="animate-spin" />
                    Checking your invite link…
                </div>
            );
        }
        if (invite && invite.valid) {
            return (
                <div
                    className="mb-6 rounded-sm border border-olive/50 bg-olive/15 px-4 py-3 text-forest"
                    data-testid="partner-invite-valid"
                >
                    <div className="flex items-center gap-2 text-[0.65rem] tracking-[0.28em] uppercase text-forest/80">
                        <Ticket size={12} /> Invited partner
                    </div>
                    <p className="mt-1 text-xs leading-relaxed">
                        You've been pre-approved to join Island Gold. Finish setting up
                        your account below — the allowlist step is skipped.
                    </p>
                </div>
            );
        }
        const msg = {
            revoked: "This invite link has been revoked. Please request a new one.",
            already_used: "This invite link has already been used.",
            expired: "This invite link has expired. Please request a new one.",
            not_found: "This invite link isn't valid.",
        }[invite?.reason || "not_found"] || "This invite link isn't valid.";
        return (
            <div
                className="mb-6 rounded-sm border border-[#b14c3c]/50 bg-[#b14c3c]/10 px-4 py-3 text-[#7a2a1e] text-xs"
                data-testid="partner-invite-invalid"
            >
                {msg}
            </div>
        );
    };

    return (
        <div
            className="min-h-[90vh] bg-forest text-cream flex items-center justify-center px-6 py-16 relative overflow-hidden"
            data-testid="partner-auth-page"
        >
            <div
                className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full blur-3xl opacity-20"
                style={{ background: "var(--ig-gold)" }}
                aria-hidden
            />
            <div className="grain absolute inset-0" />
            <div className="relative w-full max-w-md">
                <div className="text-center">
                    <span className="divider-rule text-gold">
                        <ShieldCheck size={12} /> Partner Access
                    </span>
                    <h1 className="font-serif text-5xl md:text-6xl text-cream mt-6 leading-[1.02]">
                        {mode === "signin" ? (
                            <>
                                Welcome{" "}
                                <span className="italic text-gold">back.</span>
                            </>
                        ) : (
                            <>
                                Create your{" "}
                                <span className="italic text-gold">
                                    account.
                                </span>
                            </>
                        )}
                    </h1>
                    <p className="mt-4 text-cream/70 text-sm">
                        {mode === "signin"
                            ? "Sign in to your Island Gold creator workspace."
                            : "Welcome. Create your Island Gold creator account below to get started."}
                    </p>
                </div>

                <div className="mt-10 bg-cream text-forest p-8 rounded-sm">
                    {inviteBanner()}
                    <div
                        className="flex rounded-sm overflow-hidden border border-forest/15 mb-6"
                        role="tablist"
                    >
                        {["signin", "signup"].map((m) => (
                            <button
                                key={m}
                                type="button"
                                role="tab"
                                aria-selected={mode === m}
                                onClick={() => setMode(m)}
                                data-testid={`partner-auth-tab-${m}`}
                                className={`flex-1 py-3 text-[0.7rem] tracking-[0.28em] uppercase transition-colors ${
                                    mode === m
                                        ? "bg-forest text-cream"
                                        : "bg-cream text-forest/60 hover:text-forest"
                                }`}
                            >
                                {m === "signin" ? "Sign In" : "Sign Up"}
                            </button>
                        ))}
                    </div>

                    <form onSubmit={submit} className="space-y-4">
                        <label className="block">
                            <span className="text-[0.65rem] tracking-[0.28em] uppercase text-brown inline-flex items-center gap-1.5">
                                <Mail size={12} /> Email
                            </span>
                            <input
                                type="email"
                                required
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                readOnly={!!(invite && invite.valid)}
                                data-testid="partner-auth-email"
                                className={`mt-1.5 w-full px-4 py-3 bg-cream-warm border border-forest/15 rounded-sm text-forest focus:outline-none focus:border-gold ${invite && invite.valid ? "opacity-80 cursor-not-allowed" : ""}`}
                            />
                            {invite && invite.valid && (
                                <span className="text-[0.65rem] text-brown mt-1 block">
                                    Pre-approved via invite link.
                                </span>
                            )}
                        </label>
                        {mode === "signup" && (
                            <>
                                <label className="block">
                                    <span className="text-[0.65rem] tracking-[0.28em] uppercase text-brown inline-flex items-center gap-1.5">
                                        <IdCard size={12} /> Legal Name
                                    </span>
                                    <input
                                        type="text"
                                        required
                                        autoComplete="name"
                                        value={legalName}
                                        onChange={(e) => setLegalName(e.target.value)}
                                        placeholder="Name on your government-issued ID"
                                        data-testid="partner-auth-legal-name"
                                        className="mt-1.5 w-full px-4 py-3 bg-cream-warm border border-forest/15 rounded-sm text-forest focus:outline-none focus:border-gold"
                                    />
                                    <span className="text-[0.65rem] text-brown mt-1 block">
                                        Auto-populates all agreements &amp;
                                        compliance docs. Not shown publicly.
                                    </span>
                                </label>
                                <label className="block">
                                    <span className="text-[0.65rem] tracking-[0.28em] uppercase text-brown inline-flex items-center gap-1.5">
                                        <User size={12} /> Display Name
                                        <span className="text-brown/60 normal-case tracking-normal">
                                            (optional)
                                        </span>
                                    </span>
                                    <input
                                        type="text"
                                        autoComplete="nickname"
                                        value={displayName}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        placeholder="What shows publicly — defaults to your legal first name"
                                        data-testid="partner-auth-display-name"
                                        className="mt-1.5 w-full px-4 py-3 bg-cream-warm border border-forest/15 rounded-sm text-forest focus:outline-none focus:border-gold"
                                    />
                                </label>
                            </>
                        )}
                        <label className="block">
                            <span className="text-[0.65rem] tracking-[0.28em] uppercase text-brown inline-flex items-center gap-1.5">
                                <Lock size={12} /> Password
                            </span>
                            <input
                                type="password"
                                required
                                autoComplete={
                                    mode === "signin"
                                        ? "current-password"
                                        : "new-password"
                                }
                                minLength={mode === "signup" ? 10 : undefined}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="partner-auth-password"
                                className="mt-1.5 w-full px-4 py-3 bg-cream-warm border border-forest/15 rounded-sm text-forest focus:outline-none focus:border-gold"
                            />
                            {mode === "signup" && (
                                <span className="text-[0.65rem] text-brown mt-1 block">
                                    Min 10 chars · upper + lower + digit
                                </span>
                            )}
                        </label>
                        {mode === "signup" && (
                            <label className="block">
                                <span className="text-[0.65rem] tracking-[0.28em] uppercase text-brown inline-flex items-center gap-1.5">
                                    <Lock size={12} /> Confirm Password
                                </span>
                                <input
                                    type="password"
                                    required
                                    autoComplete="new-password"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    data-testid="partner-auth-confirm"
                                    className="mt-1.5 w-full px-4 py-3 bg-cream-warm border border-forest/15 rounded-sm text-forest focus:outline-none focus:border-gold"
                                />
                            </label>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="partner-auth-submit"
                            className="btn-pill btn-gold w-full justify-center mt-2 disabled:opacity-60"
                        >
                            {loading ? (
                                <>
                                    <Loader2
                                        size={14}
                                        className="animate-spin"
                                    />{" "}
                                    Working…
                                </>
                            ) : mode === "signin" ? (
                                <>
                                    Sign In <ArrowRight size={14} />
                                </>
                            ) : (
                                <>
                                    Create Account <ArrowRight size={14} />
                                </>
                            )}
                        </button>
                    </form>

                    <p className="mt-6 text-[0.65rem] text-brown text-center leading-relaxed">
                        {mode === "signin" ? (
                            <>
                                New partner?{" "}
                                <button
                                    type="button"
                                    onClick={() => setMode("signup")}
                                    className="text-forest underline decoration-gold underline-offset-4"
                                    data-testid="partner-auth-switch-signup"
                                >
                                    Create your account
                                </button>
                            </>
                        ) : (
                            <>
                                Already have an account?{" "}
                                <button
                                    type="button"
                                    onClick={() => setMode("signin")}
                                    className="text-forest underline decoration-gold underline-offset-4"
                                    data-testid="partner-auth-switch-signin"
                                >
                                    Sign in
                                </button>
                            </>
                        )}
                    </p>
                </div>

                <p className="mt-6 text-[0.62rem] tracking-[0.28em] uppercase text-cream/50 text-center">
                    Island Gold Creator Workspace ·{" "}
                    <Link
                        to="/"
                        className="underline decoration-gold underline-offset-4"
                    >
                        Back to home
                    </Link>
                </p>
            </div>
        </div>
    );
}
